import { NavigateFunction } from "react-router-dom";
import { getApiDataToCache, getLocalStorage, setApiDataToCache } from './localStorageHelper';
import { AppConstant } from '../constant/AppConstant';
import { CategoryEnum } from "../constant/CategoryEnum";
import { useResponsive } from "./useResponsive";

let navigate: NavigateFunction;

export const isLoginUser = (): boolean => {
  return Boolean(getLocalStorage(AppConstant.authToken));
};

export const setNavigate = (nav: NavigateFunction) => {
  navigate = nav;
};

export const getNavigate = () => navigate;

export function showLog(message?: any, ...optionalParams: any[]): void {
  console.log(message, ...optionalParams);
}

export const openUrl = (url: string, newTab: boolean = false) => {
  if (!url || typeof window === "undefined") return;

  const isMobile = window.innerWidth < 768;

  const currentUrl = window.location.href;
  const targetUrl = new URL(url, window.location.origin).href;

  if (currentUrl === targetUrl) {
    return;
  }
  if (isMobile) {
    window.open(url, "_self", "");
  } else {
    window.open(url, newTab ? "_blank" : "_self", newTab ? "noopener,noreferrer" : "");
  }
};

export const getCategoryName = (category: number) => {
  return CategoryEnum.get(category) || "";
};

export const getCategoryKey = (name: string): number => {
  const entry = Array.from(CategoryEnum).find(
    ([key, value]) => value.toLowerCase() === name.toLowerCase()
  );

  return entry ? entry[0] : 1;
};


export const getProductLink = (categorySlug: string, brandSlug = "", vehicleSlug = "") => {
  let path = `/products/${categorySlug}`;
  if (brandSlug && vehicleSlug) {
    path += `/${brandSlug}/${vehicleSlug}`;
  } else if (brandSlug) {
    path += `/${brandSlug}`;
  } else if (vehicleSlug) {
    path += `/${vehicleSlug}`;
  }
  return path;
};

export const getSlugValue = (label: string) => {
  const slugified = label.trim().toLowerCase().replace(/\s+/g, "-");
  return `${slugified}`;
};

export const generateSlug = (name: string) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/\//g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const convertToEmbedUrl = (url: string) => {
  const videoIdMatch = url.match(
    /(?:\?v=|\/embed\/|\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return videoIdMatch
    ? `https://www.youtube.com/embed/${videoIdMatch[1]}`
    : url;
};

export const getYoutubeId = (url: string): string => {
  if (!url) return "";

  const regex =
    /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

  const match = url.match(regex);
  return match ? match[1] : "";
};

export const formatOtherDate = (dateString: string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
  };
  return date.toLocaleDateString("en-GB", options).replace(/,/g, "");
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  return date.toLocaleDateString("en-GB", options).replace(/,/g, "");
};

export const getTimeFormate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Time";

  const browserTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: browserTimeZone,
  });
};

export const getTimeAgo = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const intervals: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [30, "day"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"]
  ];

  let duration = diffInSeconds;

  for (const [divisor, unit] of intervals) {
    if (duration < divisor) {
      return rtf.format(-duration, unit);
    }
    duration = Math.floor(duration / divisor);
  }

  return "just now";
};

export async function fetchWithCache<T>(
  cacheKey: string,
  fetcher: () => Promise<T>
): Promise<T> {
  const cached = getApiDataToCache(cacheKey);
  if (cached) return cached;

  const data = await fetcher();
  setApiDataToCache(cacheKey, data);
  return data;
}

export const scrollToHeaderAndFocus = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  setTimeout(() => {
    const element = document.getElementById("header_search_suburb") as HTMLInputElement | null;
    if (element) {
      element.focus();
    }
  }, 400);
};

export function scopeApiCss(html: string): string {
  return html.replace(/<style>([\s\S]*?)<\/style>/gi, (match: string, css: string) => {
    // Prefix every selector with `.mobile-service-description`
    const scopedCss = css.replace(
      /(^|\})([^{]+)/g,
      (m: string, sep: string, selectors: string) => {
        if (!selectors.trim()) return m;
        const scoped = selectors
          .split(",")
          .map((s: string) => `.mobile-service-description ${s.trim()}`)
          .join(", ");
        return `${sep} ${scoped}`;
      }
    );
    return `<style>${scopedCss}</style>`;
  });
}

export const formatContent = (text: string): string => {
  return (text ?? "")
    // Headings
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")

    // Bold (**text**)
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")

    // Underline (__text__)
    .replace(/__(.*?)__/g, "<u>$1</u>")

    // Italic (*text*)
    .replace(/\*(.*?)\*/g, "<em>$1</em>")

    // Blockquote (> text)
    .replace(/^> (.*$)/gim, "<blockquote>$1</blockquote>")

    // Numbered list (1. item)
    .replace(/^\d+\.\s+(.*)$/gim, "<li>$1</li>")

    // Bullet list (- or *)
    .replace(/^[\-\*]\s+(.*)$/gim, "<li>$1</li>")

    // Wrap list items
    .replace(/(<li>.*<\/li>)/gim, "<ul>$1</ul>")
    .replace(/<\/ul>\s*<ul>/g, "") // merge multiple UL

    // Clean line breaks
    .replace(/\n\s*\n/g, "\n")
    .replace(/\n/g, "<br/>")

    .trim();
};

export const getServiceType = (service: string) => {
  switch (service) {
    case "tyre-sale":
      return 1;
    case "onsite-fitting":
      return 2;
    case "puncture-repair":
      return 3;
    case "tyre-rotation":
      return 4;
    case "wheel-balancing":
      return 5;
  }
};

export const isTimeSlotDisabled = (selectedDate: string | null, slot: string): boolean => {
  if (!selectedDate) return true;

  const today = new Date();
  const selected = new Date(selectedDate);

  if (selected.toDateString() > today.toDateString()) return false;

  const [startTime] = slot.split(" - ");
  const [hourStr, minuteStrWithAMPM] = startTime.split(".");
  let hour = parseInt(hourStr, 10);
  let minute = parseInt(minuteStrWithAMPM, 10);

  const isPM = startTime.includes("PM");
  if (isNaN(minute)) {
    const match = minuteStrWithAMPM.match(/\d+/);
    minute = match ? parseInt(match[0], 10) : 0;
  }

  if (hour === 12) hour = isPM ? 12 : 0;
  else if (isPM) hour += 12;

  const slotDate = new Date(selected);
  slotDate.setHours(hour, minute, 0, 0);
  return slotDate <= today;
};

export const formatPrice = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(value);
  if (isNaN(num)) return 0;
  return Number(num.toFixed(2));
};
